<script lang="ts">
	import { format } from 'svelte-i18n';
	import { real_id } from 'src/helpers/interface_helpers';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { gen2CloudReq } from 'src/services/gen_2_http_reqs';
	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { Button, Slider, Stack, Text, Toggle } from '@Shelly/shelly-design-system';
	import type { Device } from 'src/types/Device';
	import { capFirstChar } from 'src/helpers/utilities';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let device: Device;

	const id = real_id(device.id);
	$: settingsStore = getDeviceSettings(id);
	$: settings = $settingsStore;

	let waitForResponse = false;

	let brightness: number = settings.presence?.leds?.brightness ?? 55;
	let nightModeEnabled: boolean = settings.presence?.leds?.night_mode?.enable ?? false;
	let nightModeBrightness: number = settings.presence?.leds?.night_mode?.brightness ?? 20;
	let nightModeStart: string = settings.presence?.leds?.night_mode?.active_between?.[0] ?? '';
	let nightModeEnd: string = settings.presence?.leds?.night_mode?.active_between?.[1] ?? '';

	const brightnessA11y = {
		sliderValueText: `Current brightness value is ${brightness}`,
		ariaLabel: 'LED brightness',
		ariaLabelValue: `Current brightness value is ${brightness}`,
	};

	const nightBrightnessA11y = {
		sliderValueText: `Current night brightness value is ${nightModeBrightness}`,
		ariaLabel: 'Night mode brightness',
		ariaLabelValue: `Current night brightness value is ${nightModeBrightness}`,
	};

	const save = async () => {
		waitForResponse = true;
		const activeBetween: string[] =
			nightModeEnabled && nightModeStart && nightModeEnd ? [nightModeStart, nightModeEnd] : [];

		const res = await gen2Request(
			device,
			'Presence.SetConfig',
			{
				config: {
					leds: {
						brightness: Number(brightness),
						night_mode: {
							enable: nightModeEnabled,
							brightness: Number(nightModeBrightness),
							active_between: activeBetween,
						},
					},
				},
			},
			true,
		);
		onSettingsResponse(res, device);
		if (res.isok) {
			if (!settings.presence) settings.presence = {};
			if (!settings.presence.leds) settings.presence.leds = {};
			settings.presence.leds.brightness = Number(brightness);
			if (!settings.presence.leds.night_mode) settings.presence.leds.night_mode = {};
			settings.presence.leds.night_mode.enable = nightModeEnabled;
			settings.presence.leds.night_mode.brightness = Number(nightModeBrightness);
			settings.presence.leds.night_mode.active_between = activeBetween;
			devices_settings.update_devices_settings(id, settings);
		}
		waitForResponse = false;
	};
</script>

<Stack gap="s" class="mt-s">
	<Text styleType="body" class="mb-xs">
		{$format('presence_led_indicator_title', { default: 'LED indicator' })}
	</Text>
	<!-- <Text styleType="body-small" class="text-color-muted mb-s">
		{$format('presence_led_brightness_label', {
			values: { value: brightness },
			default: `Brightness ${brightness}%`,
		})}
	</Text> -->
	<Slider
		title={capFirstChar($format('label_brightness', { default: 'brightness' }))}
		id="slider-led-brightness-settings"
		a11yProps={brightnessA11y}
		min={0}
		withOverlay={false}
		stopValueLabel
		value={brightness}
		type="simple"
		on:value_change={(e) => (brightness = e.detail.value)}
	/>

	<hr class="mt-m mb-s" />

	<Text styleType="body" class="mb-xs">
		{$format('presence_led_night_mode_title', { default: 'Night mode' })}
	</Text>

	<Toggle
		title={$format('enable', { default: 'enable' })}
		aria-label={$format('toggle_night_mode', { default: 'Enable night mode' })}
		bind:checked={nightModeEnabled}
		size="m"
		id="toggle-night-mode-settings"
	>
		{$format('enable', { default: 'enable' })}
	</Toggle>

	{#if nightModeEnabled}
		<div class="time-fields">
			<div>
				<Text styleType="body-small" class="text-color-muted mb-xs">
					{$format('presence_led_start_time', { default: 'Start time' })}
				</Text>
				<input
					class="time-input"
					type="time"
					id="input-night-start-settings"
					bind:value={nightModeStart}
					aria-label="Night mode start time"
				/>
			</div>
			<div>
				<Text styleType="body-small" class="text-color-muted mb-xs">
					{$format('presence_led_end_time', { default: 'End time' })}
				</Text>
				<input
					class="time-input"
					type="time"
					id="input-night-end-settings"
					bind:value={nightModeEnd}
					aria-label="Night mode end time"
				/>
			</div>
		</div>

		<Slider
			id="slider-night-brightness-settings"
			title={capFirstChar($format('label_brightness', { default: 'brightness' }))}
			a11yProps={nightBrightnessA11y}
			min={0}
			withOverlay={false}
			stopValueLabel
			value={nightModeBrightness}
			type="simple"
			on:value_change={(e) => (nightModeBrightness = e.detail.value)}
		/>
	{/if}

	{#if !$showNewLayout}
		<hr class="mt-m mb-s" />
		<Button loading={waitForResponse} size="m" width="full" on:click={save} class="mb-s">
			{$format('button_save', { default: 'Save' })}
		</Button>
	{:else}
		<div class="presence-config-footer">
			<Button loading={waitForResponse} size="m" width="full" on:click={save}>
				{$format('button_save', { default: 'Save' })}
			</Button>
		</div>
	{/if}
</Stack>

<style>
	.presence-config-footer {
		position: sticky;
		bottom: 0;
		padding: 12px 0;
		background: var(--color-surface, var(--color-base));
		border-top: 1px solid var(--color-border, rgba(255, 255, 255, 0.12));
		z-index: 1;
	}

	.time-fields {
		display: flex;
		gap: 12px;
	}

	.time-fields > div {
		flex: 1;
		min-width: 0;
	}

	.time-input {
		width: 100%;
		padding: 8px 10px;
		background: var(--color-surface-secondary, rgba(255, 255, 255, 0.06));
		border: 1px solid var(--color-border, rgba(255, 255, 255, 0.12));
		border-radius: 8px;
		color: var(--color-text-primary);
		font-size: 14px;
		box-sizing: border-box;
	}

	.time-input:focus {
		outline: none;
		border-color: var(--color-primary, #0088ff);
	}
</style>
